import React, { useEffect, useState } from 'react'
import apiClient from '../../services/api'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Stack,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material'
import {
  Search,
  AttachMoney,
  Edit,
  Delete,
  Visibility,
  MoreVert,
  CheckCircle,
  Pending,
  Cancel,
  TrendingUp,
  TrendingDown,
  CalendarToday,
  Calculate as CalculateIcon,
  Check as ApproveIcon,
  Payment as PaymentIcon,
  Lock as FinalizeIcon,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import { MobileFAB, MobileMetricCard } from '../../components/Mobile'
import {
  setFilters,
  removeRecord,
  setLoading,
  setRecords,
} from '../../store/slices/payrollSlice'
import { usePullToRefresh } from '../../hooks/useSwipeGesture'
import { createStaggerAnimation } from '../../utils/mobileAnimations'
import { ConfirmDialog } from '../../components/Common/ConfirmDialog'
import { payrollAPI, employeesAPI, Employee } from '../../services/api'
import PayrollCalculationDialog from '../../components/Payroll/PayrollCalculationDialog'

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'approved', label: 'Approved' },
  { value: 'paid', label: 'Paid' },
]

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
]

/**
 * Payroll Mobile View
 * Financial cards with payment status and salary distribution
 */
export const PayrollMobileView: React.FC = () => {
  const dispatch = useAppDispatch()
  const { records, total, isLoading, filters, pagination } = useAppSelector((state) => state.payroll)

  const [searchTerm, setSearchTerm] = useState(filters.search || '')
  const [selectedStatus, setSelectedStatus] = useState(filters.status || '')
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<any>(null)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [menuRecord, setMenuRecord] = useState<any>(null)
  const [calculationDialogOpen, setCalculationDialogOpen] = useState(false)
  const [employees, setEmployees] = useState<Employee[]>([])
  const [payrollRecords, setPayrollRecords] = useState<any[]>([])
  const [selectedPeriod, setSelectedPeriod] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  })
  const [analytics, setAnalytics] = useState<any>(null)

  // Pull to refresh
  const { handlers, isPulling, isRefreshing } = usePullToRefresh(async () => {
    // Simulate refresh - in real app, this would fetch from API
    dispatch(setLoading(true))
    setTimeout(() => {
      dispatch(setLoading(false))
    }, 1000)
  })

  // Load payroll records
  useEffect(() => {
    loadPayrollData()
    loadEmployees()
    loadAnalytics()
  }, [selectedPeriod])

  const loadPayrollData = async () => {
    dispatch(setLoading(true))
    try {
      // Load records filtered by selected period
      const records = await payrollAPI.getPayrollRecords({
        year: selectedPeriod.year,
        month: selectedPeriod.month,
      })
      
      const transformedRecords = records.map((record: any) => {
        const periodStart = `${record.pay_period_year}-${String(record.pay_period_month).padStart(2, '0')}-01`
        const periodEnd = `${record.pay_period_year}-${String(record.pay_period_month).padStart(2, '0')}-${new Date(record.pay_period_year, record.pay_period_month, 0).getDate()}`
        
        return {
          payroll_id: record.id,
          employee_id: record.employee_id,
          employee_name: record.employee?.full_name || `Employee ${record.employee_id}`,
          pay_period_start: periodStart,
          pay_period_end: periodEnd,
          base_salary: record.gross_pay || 0,
          overtime_hours: 0,
          overtime_rate: 0,
          overtime_pay: 0,
          bonuses: record.payroll_items?.filter((item: any) => item.payroll_type?.category === 'bonus').reduce((sum: number, item: any) => sum + item.amount, 0) || 0,
          deductions: record.deductions || 0,
          gross_pay: record.gross_pay || 0,
          tax_deductions: 0,
          net_pay: record.net_pay || 0,
          status: (record.status === 'pending' || record.status === 'approved' || record.status === 'paid') ? record.status : 'draft',
          created_at: record.created_at,
          updated_at: record.updated_at,
          // Additional fields for display
          id: record.id,
          employee_id_number: record.employee?.employee_id || `EMP${record.employee_id.toString().padStart(3, '0')}`,
          pay_period_year: record.pay_period_year,
          pay_period_month: record.pay_period_month,
          is_draft: record.is_draft,
          pay_date: record.pay_date,
          notes: record.notes,
          payroll_items: record.payroll_items || [],
          employee: record.employee,
          period_start: periodStart,
          period_end: periodEnd,
          total_deductions: record.deductions || 0,
          total_bonuses: record.payroll_items?.filter((item: any) => item.payroll_type?.category === 'bonus').reduce((sum: number, item: any) => sum + item.amount, 0) || 0,
          payment_date: record.pay_date,
        }
      })
      
      setPayrollRecords(transformedRecords as any)
      dispatch(setRecords({ records: transformedRecords as any, total: transformedRecords.length }))
    } catch (error) {
      console.error('Failed to load payroll data:', error)
      setPayrollRecords([])
      dispatch(setRecords({ records: [], total: 0 }))
    } finally {
      dispatch(setLoading(false))
    }
  }

  const loadEmployees = async () => {
    try {
      const { employees: employeesData } = await employeesAPI.getEmployees({
        status: 'active',
      })
      setEmployees(employeesData)
    } catch (error) {
      console.error('Failed to load employees:', error)
      setEmployees([])
    }
  }

  const loadAnalytics = async () => {
    try {
      const response = await apiClient.get('/payroll/analytics/summary')
      console.log('[DEBUG] Analytics response:', response.data)
      setAnalytics(response.data)
    } catch (error) {
      console.error('Failed to load analytics:', error)
      setAnalytics(null)
    }
  }

  const handleCalculatePayroll = async (calculationData: any) => {
    try {
      const apiResult = await payrollAPI.calculatePayroll(calculationData)
      
      const transformedResult = {
        employee_id: apiResult.employee_id,
        pay_period_year: apiResult.pay_period_year,
        pay_period_month: apiResult.pay_period_month,
        calculation_summary: {
          base_pay: apiResult.calculation_summary?.base_pay || 0,
          overtime_pay: apiResult.calculation_summary?.overtime_pay || 0,
          bonus_amount: apiResult.calculation_summary?.bonus_amount || 0,
          total_deductions: apiResult.calculation_summary?.total_deductions || apiResult.total_deductions,
          gross_pay: apiResult.calculation_summary?.gross_pay || apiResult.gross_pay,
          net_pay: apiResult.calculation_summary?.net_pay || apiResult.net_pay,
          compensation_structure_type: apiResult.calculation_summary?.compensation_structure_type || 'unknown',
          base_rate: apiResult.calculation_summary?.base_rate || 0,
          hours_worked: apiResult.calculation_summary?.hours_worked || 0,
          days_worked: apiResult.calculation_summary?.days_worked || 0,
          overtime_hours: apiResult.calculation_summary?.overtime_hours || 0,
        },
        payroll_items: (apiResult.payroll_items || []).map((item: any) => ({
          payroll_type_id: item.payroll_type_id,
          amount: item.amount,
          hours: item.hours,
          rate: item.rate,
          description: item.description || '',
        })),
        warnings: apiResult.warnings || [],
      }
      
      return transformedResult
    } catch (error) {
      console.error('Payroll calculation failed:', error)
      throw error
    }
  }

  const handleCreatePayrollRecord = async (calculationResult: any) => {
    try {
      // Use the manual payroll calculate endpoint to create draft records
      const response = await apiClient.post('/payroll/manual/calculate', {
        employee_ids: [calculationResult.employee_id],
        pay_period_year: calculationResult.pay_period_year,
        pay_period_month: calculationResult.pay_period_month,
      })
      
      console.log('Payroll record created:', response.data)
      
      // Update the selected period to match the created record so it shows up
      setSelectedPeriod({
        year: calculationResult.pay_period_year,
        month: calculationResult.pay_period_month,
      })
      
      // Reload the data to show the new record
      await loadPayrollData()
      setCalculationDialogOpen(false)
    } catch (error) {
      console.error('Failed to create payroll record:', error)
      throw error
    }
  }

  // Use payrollRecords as the base data source
  const allRecords = payrollRecords.length > 0 ? payrollRecords : records
  
  // Apply filters manually
  let displayRecords = allRecords
  
  // Apply search filter
  if (searchTerm) {
    displayRecords = displayRecords.filter((r: any) =>
      r.employee_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.employee_id_number?.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }
  
  // Apply status filter
  if (selectedStatus) {
    displayRecords = displayRecords.filter((r: any) =>
      r.status?.toLowerCase() === selectedStatus.toLowerCase()
    )
  }
  
  // Debug: Log all record statuses
  console.log('[DEBUG] All records:', allRecords?.map((r: any) => ({
    id: r.id,
    status: r.status,
    net_pay: r.net_pay,
    employee: r.employee_name
  })))
  
  console.log('[DEBUG] Filtered records:', displayRecords?.map((r: any) => ({
    id: r.id,
    status: r.status,
    net_pay: r.net_pay,
    employee: r.employee_name
  })))
  
  // Calculate totals from all records (not filtered) - match status case-insensitively
  // Convert net_pay to number since it might be a string from the API
  const totalPaid = allRecords?.filter((r: any) => r.status?.toLowerCase() === 'paid').reduce((sum: number, r: any) => sum + (parseFloat(r.net_pay) || 0), 0) || 0
  const totalPending = allRecords?.filter((r: any) => r.status?.toLowerCase() === 'pending').reduce((sum: number, r: any) => sum + (parseFloat(r.net_pay) || 0), 0) || 0
  const totalApproved = allRecords?.filter((r: any) => r.status?.toLowerCase() === 'approved').reduce((sum: number, r: any) => sum + (parseFloat(r.net_pay) || 0), 0) || 0
  const totalDraft = allRecords?.filter((r: any) => r.status?.toLowerCase() === 'draft').reduce((sum: number, r: any) => sum + (parseFloat(r.net_pay) || 0), 0) || 0
  const totalCalculated = allRecords?.filter((r: any) => r.status?.toLowerCase() === 'calculated').reduce((sum: number, r: any) => sum + (parseFloat(r.net_pay) || 0), 0) || 0
  const avgSalary = allRecords?.length > 0 ? allRecords.reduce((sum: number, r: any) => sum + (parseFloat(r.net_pay) || 0), 0) / allRecords.length : 0
  const pendingCount = allRecords?.filter((r: any) => r.status?.toLowerCase() === 'pending').length || 0
  
  // Include draft and calculated records in pending
  const totalPendingAll = totalPending + totalDraft + totalCalculated
  const pendingCountAll = allRecords?.filter((r: any) =>
    ['pending', 'draft', 'calculated'].includes(r.status?.toLowerCase())
  ).length || 0
  
  console.log('[DEBUG] Summary stats:', {
    totalPaid,
    totalPending,
    totalDraft,
    totalCalculated,
    totalPendingAll,
    pendingCount,
    pendingCountAll,
    recordsCount: allRecords?.length,
    filteredCount: displayRecords?.length
  })

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setSearchTerm(value)
    setTimeout(() => {
      dispatch(setFilters({ search: value }))
    }, 500)
  }

  const handleStatusFilter = (status: string) => {
    setSelectedStatus(status)
    dispatch(setFilters({ status: status || undefined }))
  }

  const handlePreviousMonth = () => {
    setSelectedPeriod(prev => {
      if (prev.month === 1) {
        return { year: prev.year - 1, month: 12 }
      }
      return { ...prev, month: prev.month - 1 }
    })
  }

  const handleNextMonth = () => {
    setSelectedPeriod(prev => {
      if (prev.month === 12) {
        return { year: prev.year + 1, month: 1 }
      }
      return { ...prev, month: prev.month + 1 }
    })
  }

  const handleMonthChange = (month: number) => {
    setSelectedPeriod(prev => ({ ...prev, month }))
  }

  const handleYearChange = (year: number) => {
    setSelectedPeriod(prev => ({ ...prev, year }))
  }

  const getMonthLabel = (month: number) => {
    return MONTHS.find(m => m.value === month)?.label || ''
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, record: any) => {
    event.stopPropagation()
    setMenuAnchorEl(event.currentTarget)
    setMenuRecord(record)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
    setMenuRecord(null)
  }

  const handleViewRecord = (record: any) => {
    setSelectedRecord(record)
    handleMenuClose()
    // Show alert with record details for now
    alert(`Payroll Details:\n\nEmployee: ${record.employee_name}\nGross Pay: Rs. ${record.gross_pay?.toLocaleString()}\nDeductions: Rs. ${record.total_deductions?.toLocaleString()}\nNet Pay: Rs. ${record.net_pay?.toLocaleString()}\nStatus: ${record.status}`)
  }

  const handleEditRecord = (record: any) => {
    setSelectedRecord(record)
    handleMenuClose()
    // Show alert for now - full edit dialog to be implemented
    alert(`Edit functionality for ${record.employee_name} will be available soon.`)
  }

  const handleDeleteRecord = (record: any) => {
    setSelectedRecord(record)
    setConfirmDialogOpen(true)
    handleMenuClose()
  }

  const handleFinalizeRecord = async (record: any) => {
    handleMenuClose()
    try {
      await apiClient.post('/payroll/manual/finalize', {
        payroll_record_ids: [record.id]
      })
      alert(`Payroll record for ${record.employee_name} has been finalized.`)
      await loadPayrollData()
    } catch (error: any) {
      console.error('Failed to finalize record:', error)
      alert(error.response?.data?.detail || 'Failed to finalize payroll record.')
    }
  }

  const handleApproveRecord = async (record: any) => {
    handleMenuClose()
    try {
      await apiClient.post(`/payroll/${record.id}/approve`)
      alert(`Payroll record for ${record.employee_name} has been approved.`)
      await loadPayrollData()
    } catch (error: any) {
      console.error('Failed to approve record:', error)
      alert(error.response?.data?.detail || 'Failed to approve payroll record.')
    }
  }

  const handleMarkAsPaid = async (record: any) => {
    handleMenuClose()
    try {
      await apiClient.post(`/payroll/${record.id}/pay`)
      alert(`Payroll record for ${record.employee_name} has been marked as paid.`)
      await loadPayrollData()
    } catch (error: any) {
      console.error('Failed to mark as paid:', error)
      alert(error.response?.data?.detail || 'Failed to mark payroll as paid.')
    }
  }

  const confirmDelete = async () => {
    if (selectedRecord) {
      try {
        // Delete draft payroll records using the manual payroll endpoint
        await apiClient.delete(`/payroll/manual/${selectedRecord.id}`)
        
        // Remove from Redux store
        dispatch(removeRecord(selectedRecord.id))
        
        // Reload data to ensure consistency
        await loadPayrollData()
        
        setConfirmDialogOpen(false)
        setSelectedRecord(null)
      } catch (error) {
        console.error('Failed to delete payroll record:', error)
        alert('Failed to delete payroll record. Please try again.')
      }
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return 'success'
      case 'approved':
        return 'info'
      case 'pending':
        return 'warning'
      case 'rejected':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return <CheckCircle sx={{ fontSize: 20 }} />
      case 'approved':
        return <CheckCircle sx={{ fontSize: 20 }} />
      case 'pending':
        return <Pending sx={{ fontSize: 20 }} />
      case 'rejected':
        return <Cancel sx={{ fontSize: 20 }} />
      default:
        return null
    }
  }

  return (
    <Box sx={{ pb: 10, bgcolor: '#f5f7fa', minHeight: '100vh', pt: 2 }}>

      {/* Records Section */}
      {true && (
        <Box
          onTouchStart={handlers.onTouchStart as any}
          onTouchMove={handlers.onTouchMove as any}
          onTouchEnd={handlers.onTouchEnd as any}
        >
          {/* Pull to Refresh Indicator */}
          {isPulling && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                height: 60,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'white',
                zIndex: 1000,
              }}
            >
              <Typography variant="caption">
                {isRefreshing ? 'Refreshing...' : 'Pull to refresh'}
              </Typography>
            </Box>
          )}

          {/* Period Selector */}
          <Box sx={{ px: 3, mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                p: 1.5,
                bgcolor: 'white',
                borderRadius: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              }}
            >
              <IconButton
                size="small"
                onClick={handlePreviousMonth}
                sx={{
                  bgcolor: '#f5f5f5',
                  '&:hover': { bgcolor: '#e0e0e0' },
                  flexShrink: 0,
                }}
              >
                <ChevronLeft />
              </IconButton>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
                <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                  <Select
                    value={selectedPeriod.month}
                    onChange={(e) => handleMonthChange(Number(e.target.value))}
                    sx={{
                      borderRadius: '12px',
                      bgcolor: '#f8f9fa',
                      '& fieldset': { border: 'none' },
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}
                  >
                    {MONTHS.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 80, flexShrink: 0 }}>
                  <Select
                    value={selectedPeriod.year}
                    onChange={(e) => handleYearChange(Number(e.target.value))}
                    sx={{
                      borderRadius: '12px',
                      bgcolor: '#f8f9fa',
                      '& fieldset': { border: 'none' },
                      fontWeight: 600,
                      fontSize: '0.875rem',
                    }}
                  >
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <IconButton
                size="small"
                onClick={handleNextMonth}
                sx={{
                  bgcolor: '#f5f5f5',
                  '&:hover': { bgcolor: '#e0e0e0' },
                  flexShrink: 0,
                }}
              >
                <ChevronRight />
              </IconButton>
            </Box>
          </Box>

          {/* Search */}
          <Box sx={{ px: 3 }}>
            <TextField
              fullWidth
              placeholder="Search by employee..."
              value={searchTerm}
              onChange={handleSearch}
              size="small"
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  bgcolor: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: 'none',
                  '& fieldset': {
                    border: 'none',
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Status Filter Chips */}
            <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 2, mb: 1 }}>
              {STATUS_OPTIONS.map((status) => (
                <Chip
                  key={status.value}
                  label={status.label}
                  onClick={() => handleStatusFilter(status.value)}
                  sx={{
                    borderRadius: '20px',
                    fontWeight: 600,
                    fontSize: '0.813rem',
                    px: 1,
                    bgcolor: selectedStatus === status.value ? '#667eea' : 'white',
                    color: selectedStatus === status.value ? 'white' : 'text.primary',
                    border: selectedStatus === status.value ? 'none' : '1px solid #e0e0e0',
                    boxShadow: selectedStatus === status.value ? '0 2px 8px rgba(102, 126, 234, 0.3)' : 'none',
                    '&:hover': {
                      bgcolor: selectedStatus === status.value ? '#5568d3' : '#f5f5f5',
                    },
                  }}
                />
              ))}
            </Stack>
          </Box>

          {/* Summary Metrics */}
          <Box sx={{ px: 3, mb: 3 }}>
            <Stack spacing={2}>
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '20px',
                  p: 3,
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.25)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Total Paid
                  </Typography>
                  <CheckCircle sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: 24 }} />
                </Box>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                  Rs. {Math.round(totalPaid).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  This month
                </Typography>
              </Box>

              <Box
                sx={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  borderRadius: '20px',
                  p: 3,
                  boxShadow: '0 8px 24px rgba(240, 147, 251, 0.25)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Pending Payments
                  </Typography>
                  <Pending sx={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: 24 }} />
                </Box>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                  Rs. {Math.round(totalPendingAll).toLocaleString()}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  {pendingCountAll} records awaiting
                </Typography>
              </Box>
            </Stack>
          </Box>

          {/* Payroll Records List */}
          <Box sx={{ px: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {getMonthLabel(selectedPeriod.month)} {selectedPeriod.year}
              </Typography>
              <Chip
                label={`${displayRecords.length} records`}
                size="small"
                sx={{
                  bgcolor: '#f0f4ff',
                  color: '#667eea',
                  fontWeight: 600,
                  borderRadius: '12px',
                }}
              />
            </Box>
            {isLoading && displayRecords.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">Loading payroll records...</Typography>
              </Box>
            ) : displayRecords && displayRecords.length > 0 ? (
              <Stack spacing={2.5}>
                {displayRecords.map((record: any, index: number) => (
                  <Card
                    key={record.id}
                    onClick={() => handleViewRecord(record)}
                    sx={{
                      borderRadius: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                      border: 'none',
                      overflow: 'hidden',
                      ...createStaggerAnimation(index, 30),
                      '&:active': {
                        transform: 'scale(0.97)',
                      },
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    {/* Gradient Header Background - Consistent darker teal gradient for all cards */}
                    <Box
                      sx={{
                        height: 120,
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'flex-end',
                        p: 2.5,
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: -30,
                          right: -30,
                          width: 120,
                          height: 120,
                          borderRadius: '50%',
                          bgcolor: 'rgba(255, 255, 255, 0.15)',
                        },
                      }}
                    >
                      <Box sx={{ position: 'relative', zIndex: 1, width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Box>
                            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, mb: 0.5 }}>
                              {record.employee_name}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                              {record.employee_id_number}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, record)}
                            sx={{
                              color: 'white',
                              bgcolor: 'rgba(255, 255, 255, 0.2)',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.3)',
                              },
                            }}
                          >
                            <MoreVert />
                          </IconButton>
                        </Box>
                      </Box>
                    </Box>

                    <CardContent sx={{ p: 2.5 }}>
                      {/* Period Info */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary">
                          {record.period_start && record.period_end
                            ? `${new Date(record.period_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(record.period_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                            : 'N/A'}
                        </Typography>
                      </Box>

                      {/* Payment Details Grid */}
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: 2,
                        mb: 2,
                        p: 2,
                        bgcolor: '#f8f9fa',
                        borderRadius: '12px',
                      }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Gross Pay
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>
                            Rs. {record.gross_pay?.toLocaleString()}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                            Deductions
                          </Typography>
                          <Typography variant="body2" fontWeight={600} color="error.main">
                            Rs. {record.total_deductions?.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Net Pay - Prominent */}
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: 2,
                          bgcolor: '#f0f4ff',
                          borderRadius: '12px',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600} color="text.secondary">
                          Net Pay
                        </Typography>
                        <Typography variant="h6" fontWeight={700} color="primary.main">
                          Rs. {record.net_pay?.toLocaleString()}
                        </Typography>
                      </Box>

                      {/* Status Badge */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip
                          label={record.status}
                          size="small"
                          icon={getStatusIcon(record.status)}
                          sx={{
                            borderRadius: '12px',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            bgcolor:
                              record.status?.toLowerCase() === 'paid'
                                ? '#e8f5e9'
                                : record.status?.toLowerCase() === 'approved'
                                ? '#e3f2fd'
                                : '#fff3e0',
                            color:
                              record.status?.toLowerCase() === 'paid'
                                ? '#2e7d32'
                                : record.status?.toLowerCase() === 'approved'
                                ? '#1976d2'
                                : '#f57c00',
                            border: 'none',
                            '& .MuiChip-icon': {
                              color: 'inherit',
                            },
                          }}
                        />
                        {record.payment_date && (
                          <Typography variant="caption" color="text.secondary">
                            Paid on {new Date(record.payment_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </Typography>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : (
              <Card sx={{
                p: 5,
                textAlign: 'center',
                borderRadius: '20px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              }}>
                <AttachMoney sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  No payroll records found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || selectedStatus
                    ? 'Try adjusting your filters'
                    : 'Create your first payroll record to get started'}
                </Typography>
              </Card>
            )}
          </Box>

          {/* Floating Action Button */}
          <Box
            onClick={() => setCalculationDialogOpen(true)}
            sx={{
              position: 'fixed',
              bottom: 80,
              right: 20,
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              color: 'white',
              borderRadius: '20px',
              px: 3,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              boxShadow: '0 8px 24px rgba(17, 153, 142, 0.4)',
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:active': {
                transform: 'scale(0.95)',
              },
            }}
          >
            <CalculateIcon />
            <Typography variant="body2" fontWeight={600}>
              Calculate Payroll
            </Typography>
          </Box>
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            minWidth: 200,
          },
        }}
      >
        <MenuItem onClick={() => menuRecord && handleViewRecord(menuRecord)}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        
        {menuRecord?.status?.toLowerCase() === 'draft' && (
          <>
            <MenuItem onClick={() => menuRecord && handleEditRecord(menuRecord)}>
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit Record</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => menuRecord && handleFinalizeRecord(menuRecord)}>
              <ListItemIcon>
                <FinalizeIcon fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Finalize</ListItemText>
            </MenuItem>
          </>
        )}
        
        {menuRecord?.status?.toLowerCase() === 'calculated' && (
          <MenuItem onClick={() => menuRecord && handleApproveRecord(menuRecord)}>
            <ListItemIcon>
              <ApproveIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Approve</ListItemText>
          </MenuItem>
        )}
        
        {menuRecord?.status?.toLowerCase() === 'approved' && (
          <MenuItem onClick={() => menuRecord && handleMarkAsPaid(menuRecord)}>
            <ListItemIcon>
              <PaymentIcon fontSize="small" color="primary" />
            </ListItemIcon>
            <ListItemText>Mark as Paid</ListItemText>
          </MenuItem>
        )}
        
        <Divider />
        
        {['draft', 'calculated'].includes(menuRecord?.status?.toLowerCase()) && (
          <MenuItem onClick={() => menuRecord && handleDeleteRecord(menuRecord)} sx={{ color: 'error.main' }}>
            <ListItemIcon>
              <Delete fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Payroll Record"
        message={`Are you sure you want to delete this payroll record for ${selectedRecord?.employee_name}? This action cannot be undone.`}
      />

      {/* Payroll Calculation Dialog */}
      <PayrollCalculationDialog
        open={calculationDialogOpen}
        onClose={() => setCalculationDialogOpen(false)}
        onCalculate={handleCalculatePayroll}
        onCreateRecord={handleCreatePayrollRecord}
        employees={employees as any}
        loading={isLoading}
      />
    </Box>
  )
}
